import { useState } from 'react'
import { CloudWarning, XCircle } from '@phosphor-icons/react'
import { Banner, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useDevices } from '../api/setup'
import {
  SYNC_PROBLEMS_PERM,
  useDismissSyncProblem,
  useSyncProblems,
  type SyncProblemRow,
  type SyncProblemStatus,
} from '../api/sync'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Code, Facts, Status, When } from '../components/bits'
import { humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'
import { explainSyncProblem, SYNC_RESULT_LABEL } from '../model/syncProblems'

const LIMIT = 25

const phone = (p: SyncProblemRow) =>
  [p.deviceCode, p.deviceName].filter(Boolean).join(' · ') || 'Unknown phone'

/**
 * Sync problems: phone entries the server did not apply (refused, waiting for another phone's
 * data, or a server failure), so the office can see and clear them without an engineer.
 */
export function SyncProblemsPage() {
  const [f, setF, { reset, active }] = useUrlState({
    status: 'OPEN',
    result: '',
    deviceId: '',
    page: 1,
    open: '',
  })
  const devices = useDevices({ page: 1, limit: 200 })
  const q = useSyncProblems({
    page: f.page,
    limit: LIMIT,
    status: (f.status || 'ALL') as SyncProblemStatus | 'ALL',
    result: f.result || undefined,
    deviceId: f.deviceId || undefined,
  })
  const rows = q.data?.rows ?? []
  const selected = rows.find((r) => r.id === f.open) ?? null

  const columns: Column<SyncProblemRow>[] = [
    {
      id: 'seen',
      header: 'Last seen',
      cell: (r) => <When at={r.lastSeenAt} relative />,
      width: '120px',
    },
    {
      id: 'phone',
      header: 'Phone',
      cell: (r) => <span className="text-[14px]">{phone(r)}</span>,
    },
    {
      id: 'what',
      header: 'Entry',
      cell: (r) => (
        <span className="flex flex-col">
          <span>{r.commandType ? humanize(r.commandType) : '—'}</span>
          {r.actorName && <span className="text-[12.5px] text-muted">by {r.actorName}</span>}
        </span>
      ),
    },
    {
      id: 'problem',
      header: 'Problem',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-medium">{explainSyncProblem(r).title}</span>
          <Code className="text-muted">{r.errorCode}</Code>
        </span>
      ),
    },
    {
      id: 'attempts',
      header: 'Tries',
      cell: (r) => <span className="tabular">{r.attempts}</span>,
      hideBelow: 'md',
      width: '70px',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <Status status={r.status} label={humanize(r.status)} />,
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Sync problems"
      description="Entries a phone sent that the server did not save. Most clear by themselves once the other phone syncs; the rest need someone to look and either fix the data or dismiss them."
    >
      <DataTable<SyncProblemRow>
        caption="Sync problems"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Status"
              allLabel="Any status"
              value={f.status as '' | SyncProblemStatus}
              onChange={(status) => setF({ status })}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'RESOLVED', label: 'Resolved' },
                { value: 'DISMISSED', label: 'Dismissed' },
              ]}
            />
            <SelectFilter
              label="Kind"
              value={f.result}
              onChange={(result) => setF({ result })}
              options={Object.entries(SYNC_RESULT_LABEL).map(([value, label]) => ({
                value,
                label,
              }))}
            />
            <SelectFilter
              label="Phone"
              value={f.deviceId}
              onChange={(deviceId) => setF({ deviceId })}
              options={(devices.data?.rows ?? []).map((d) => ({
                value: d.id,
                label: `${d.codePrefix} · ${d.groupName ?? d.locationName ?? ''}`,
              }))}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<CloudWarning />}
            title={
              f.status === 'OPEN' && !f.result && !f.deviceId
                ? 'No open sync problems'
                : 'Nothing matches'
            }
            description={
              f.status === 'OPEN'
                ? 'Every entry the phones sent has been saved.'
                : 'Try clearing the filters.'
            }
          />
        }
      />
      <ProblemDrawer problem={selected} onClose={() => setF({ open: '', page: f.page })} />
    </ConsolePage>
  )
}

function ProblemDrawer({
  problem: p,
  onClose,
}: {
  problem: SyncProblemRow | null
  onClose: () => void
}) {
  const [dismissing, setDismissing] = useState(false)
  const dismiss = useDismissSyncProblem()
  const ex = p ? explainSyncProblem(p) : null
  return (
    <DetailDrawer
      open={Boolean(p)}
      onClose={onClose}
      eyebrow="Sync problem"
      title={ex?.title ?? 'Sync problem'}
      meta={p && <Status status={p.status} label={humanize(p.status)} />}
      actions={
        p?.status === 'OPEN' && (
          <ActionButton
            perm={SYNC_PROBLEMS_PERM}
            variant="secondary"
            leadingIcon={<XCircle />}
            onClick={() => setDismissing(true)}
          >
            Dismiss
          </ActionButton>
        )
      }
    >
      {p && ex && (
        <div className="flex flex-col gap-5">
          <Banner tone={p.result === 'WAIT_FOR_PARENT' ? 'info' : 'warn'} title={ex.title}>
            {ex.action}
          </Banner>
          {p.status === 'DISMISSED' && (
            <Banner tone="info" title="Dismissed">
              {p.dismissedByName ?? 'Someone'} dismissed this <When at={p.dismissedAt} relative />
              {p.dismissReason && <>: {p.dismissReason}</>}. Nothing was saved from it. If the phone
              gets it through later, it turns Resolved.
            </Banner>
          )}
          {p.status === 'RESOLVED' && (
            <Banner tone="success" title="Resolved">
              The phone sent it again and the server saved it ({humanize(p.resolution ?? 'APPLIED')}
              )<> </>
              <When at={p.resolvedAt} relative />.
            </Banner>
          )}
          <Facts
            columns={2}
            items={[
              { label: 'Phone', value: phone(p) },
              { label: 'Entry', value: p.commandType ? humanize(p.commandType) : '—' },
              { label: 'Made by', value: p.actorName ?? p.actorId ?? '—' },
              { label: 'Made on the phone', value: <When at={p.occurredAt} /> },
              { label: 'First seen', value: <When at={p.firstSeenAt} /> },
              { label: 'Last seen', value: <When at={p.lastSeenAt} /> },
              { label: 'Tries', value: p.attempts },
              { label: 'Kind', value: SYNC_RESULT_LABEL[p.result] ?? p.result },
              { label: 'Error code', value: p.errorCode, mono: true },
            ]}
          />
          {p.errorMessage && (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-muted">Server message</h3>
              <p className="whitespace-pre-wrap break-words rounded-card border border-rule bg-surface px-3 py-2 font-mono text-[13px] text-ink">
                {p.errorMessage}
              </p>
            </section>
          )}
          {p.waitingOn.length > 0 && (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-muted">Waiting for these entries</h3>
              <ul className="flex flex-col gap-0.5">
                {p.waitingOn.map((w) => (
                  <li key={w}>
                    <Code>{w}</Code>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {p.payloadSummary && Object.keys(p.payloadSummary).length > 0 && (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-muted">
                What the phone sent (summary)
              </h3>
              <Facts
                columns={2}
                items={Object.entries(p.payloadSummary).map(([k, v]) => ({
                  label: humanize(k),
                  value: v === null || v === undefined ? '—' : String(v),
                  mono: true,
                }))}
              />
            </section>
          )}
          <Facts
            columns={2}
            items={[
              { label: 'Command id', value: p.commandId, mono: true },
              { label: 'Collection day id', value: p.sessionId ?? '—', mono: true },
            ]}
          />
          <ConfirmDialog
            open={dismissing}
            onClose={() => setDismissing(false)}
            title="Dismiss this sync problem?"
            description="Use this once you have checked it and nothing more needs to be done here."
            confirmLabel="Dismiss"
            loading={dismiss.isPending}
            reason={{
              label: 'Reason',
              placeholder: 'e.g. Same day opened twice; the other one is saved',
              minLength: 3,
              multiline: true,
            }}
            onConfirm={(reason) =>
              dismiss
                .mutateAsync({ id: p.id, reason: reason.slice(0, 500) })
                .then(() => setDismissing(false))
            }
          >
            <ul className="list-disc space-y-1 pl-5 text-[14px] text-ink">
              <li>Nothing from this entry is saved. Dismissing only clears it from this list.</li>
              <li>
                The phone still has it. A supervisor can set it aside on the phone’s Sync screen so
                the rest of its work keeps sending.
              </li>
              <li>If the phone gets it through later, it shows here as Resolved.</li>
            </ul>
          </ConfirmDialog>
        </div>
      )}
    </DetailDrawer>
  )
}
